import { useState, useTransition } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, CheckCircle2, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input, Label, Textarea } from "@/components/ui/input";
import { useDict } from "@/lib/i18n/context";
import { resolveApiError } from "@/lib/api-error";

export function ContactPage() {
  const t = useDict();
  const [pending, startTransition] = useTransition();
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);
    setError(null);
    startTransition(async () => {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: String(fd.get("name") || "").trim(),
          email: String(fd.get("email") || "").trim(),
          subject: String(fd.get("subject") || "").trim(),
          message: String(fd.get("message") || "").trim(),
        }),
      });
      const data = (await res.json()) as { error?: string; code?: string };
      if (!res.ok) {
        setError(resolveApiError(t, data, "contactFailed"));
        return;
      }
      setSent(true);
      form.reset();
    });
  }

  return (
    <div className="mx-auto max-w-xl">
      <Link
        to="/"
        className="mb-6 inline-flex items-center gap-2 text-sm text-muted hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4 rtl:rotate-180" />
        {t.common.back}
      </Link>

      <div className="mb-8">
        <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand-50 text-brand-600">
          <Mail className="h-6 w-6" />
        </span>
        <h1 className="mt-4 text-3xl font-bold">{t.contact.title}</h1>
        <p className="mt-2 text-muted">{t.contact.subtitle}</p>
      </div>

      {sent ? (
        <div className="rounded-2xl border border-border bg-card p-8 text-center">
          <CheckCircle2 className="mx-auto h-12 w-12 text-brand-600" />
          <h2 className="mt-4 text-xl font-semibold">{t.contact.successTitle}</h2>
          <p className="mt-2 text-sm text-muted">{t.contact.successBody}</p>
          <Button className="mt-6" variant="outline" onClick={() => setSent(false)}>
            {t.contact.sendAnother}
          </Button>
        </div>
      ) : (
        <form
          onSubmit={onSubmit}
          className="space-y-4 rounded-2xl border border-border bg-card p-6 shadow-sm"
        >
          <div>
            <Label htmlFor="name">{t.contact.name}</Label>
            <Input id="name" name="name" required maxLength={200} className="mt-1.5" />
          </div>
          <div>
            <Label htmlFor="email">{t.contact.email}</Label>
            <Input
              id="email"
              name="email"
              type="email"
              required
              maxLength={200}
              className="mt-1.5"
            />
          </div>
          <div>
            <Label htmlFor="subject">{t.contact.subject}</Label>
            <Input id="subject" name="subject" required maxLength={200} className="mt-1.5" />
          </div>
          <div>
            <Label htmlFor="message">{t.contact.message}</Label>
            <Textarea
              id="message"
              name="message"
              required
              rows={6}
              maxLength={5000}
              className="mt-1.5"
            />
          </div>
          {error && <p className="text-sm text-red-600">{error}</p>}
          <Button type="submit" disabled={pending} className="w-full">
            {pending ? t.contact.sending : t.contact.submit}
          </Button>
        </form>
      )}
    </div>
  );
}
